import { useMemo } from "react"

import type { QueryPlotDefBase } from "types/dashboard.types"
import type { EntityTelemetry } from "types/telemetries.types"
import type { FieldQualityMap } from "types/quality.types"
import type { DateRange } from "types/date.types"

import {
  applyDataTransform,
  divideChartAndTooltipData,
  extractQualityMetadata,
  filterTelemetryDataByTime,
  formatTelemetryData,
  getFill,
} from "widgets/plots/helpers"
import { getForecastThresholdDate } from "helpers/utils/dateUtils"
import PlotTabLegend from "widgets/plots/common/PlotTabLegend"
import { useDashboardContext } from "helpers/hooks/useDashboardContext"
import { usePlotTabVisibility } from "./usePlotTabVisibility"

interface UsePlotProcessorProps {
  data: EntityTelemetry[]
  def: QueryPlotDefBase
  entityIds: number[]
  isRawData: boolean
  dateRange: DateRange | null
  error?: unknown
}

interface UsePlotProcessorReturn {
  formattedDef: QueryPlotDefBase
  tabLegend: JSX.Element | undefined
  fieldQualityMap: FieldQualityMap | undefined
  plotData: EntityTelemetry[]
  tooltipData: EntityTelemetry[]
  shouldRender: boolean
}

export const usePlotProcessor = ({
  data,
  def,
  entityIds,
  isRawData,
  dateRange,
  error,
}: UsePlotProcessorProps): UsePlotProcessorReturn => {
  const { view, reqConfig } = def
  const { forecastFields, tooltipOnlyFields, fieldConversions } = reqConfig

  const { plotLayout } = useDashboardContext()

  const formattedDef = useMemo(() => {
    if (view.type !== "xy") return def
    const fill = getFill(view, reqConfig, entityIds)
    return { ...def, view: { ...view, properties: { ...view.properties, fill } } }
  }, [def, view, reqConfig, entityIds])

  const forecastThresholdDate = useMemo(
    () => getForecastThresholdDate(dateRange),
    [dateRange],
  )

  const tabVisibility = usePlotTabVisibility(def.tabsConfig)
  const visibleFields = useMemo(() => {
    if (!def.tabsConfig) return undefined
    return tabVisibility.visibleFields
  }, [def.tabsConfig, tabVisibility])

  const tabLegend = useMemo(() => {
    if (!def.tabsConfig) return undefined
    return <PlotTabLegend tabsConfig={def.tabsConfig} visibility={tabVisibility} />
  }, [def.tabsConfig, tabVisibility])

  const tabData = useMemo(
    () =>
      visibleFields ? data.filter((r) => !r._field || visibleFields.has(r._field)) : data,
    [data, visibleFields],
  )

  const timeFilteredData = useMemo(
    () =>
      filterTelemetryDataByTime(
        tabData,
        forecastFields,
        view.nameKey,
        forecastThresholdDate,
        isRawData,
      ),
    [tabData, forecastFields, view.nameKey, forecastThresholdDate, isRawData],
  )

  const { filteredData, fieldQualityMap } = useMemo(
    () => extractQualityMetadata(timeFilteredData ?? [], isRawData),
    [timeFilteredData, isRawData],
  )

  const processedData = useMemo(() => {
    const baseFormattedData = formatTelemetryData(filteredData, fieldConversions)
    return applyDataTransform(baseFormattedData, view)
  }, [filteredData, fieldConversions, view])

  const [plotData, tooltipData] = useMemo(
    () => divideChartAndTooltipData(processedData, tooltipOnlyFields),
    [processedData, tooltipOnlyFields],
  )

  // A plot's dependencies must all be rendering (h !== 0 in the shared layout)
  // for it to render. While the layout isn't available (e.g. outside a dashboard)
  // dependencies are treated as satisfied. The dependency plot publishes its
  // render state via `dispatchPlotLayout`, so this stays in sync as data loads.
  const dependenciesSatisfied = useMemo(() => {
    const deps = def.dependencies
    if (!deps || deps.length === 0) return true
    if (!plotLayout) return true
    return deps.every((dep) => (plotLayout[dep]?.h ?? 0) !== 0)
  }, [def.dependencies, plotLayout])

  // Table-stats flagged with `showWhenEmpty` always render: with no data they
  // show placeholder dashes for each configured row instead of disappearing.
  const showWhenEmpty =
    view.type === "table-stat" && view.properties.showWhenEmpty === true

  // Tabbed plot that has data on some tabs but not on the active one:
  // keep it mounted so the tab bar stays and the user can switch.
  // Collapse only when all tabs are empty.
  const isActiveTabEmpty =
    Boolean(def.tabsConfig) && data.length > 0 && !(plotData && plotData.length > 0)

  const shouldRender =
    dependenciesSatisfied &&
    (showWhenEmpty ||
      isActiveTabEmpty ||
      Boolean(error || (plotData && plotData.length > 0)))

  return {
    formattedDef,
    tabLegend,
    fieldQualityMap,
    plotData,
    tooltipData,
    shouldRender,
  }
}
